import { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { IconButton } from '@radix-ui/themes';
import { MagnifyingGlassIcon } from '@/components/icons';
import { useDebounce } from '../../hooks/useDebounce';
import { useGlobalHotkey } from '../../hooks/useGlobalHotkey';
import { SearchTray } from '../search/SearchTray';
import searchTrayStyles from '../search/SearchTray.module.css';

const CloseSmallIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <mask id="mask0_close_search" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
      <rect width="24" height="24" fill="#D9D9D9" />
    </mask>
    <g mask="url(#mask0_close_search)">
      <path d="M8.4 17L7 15.6L10.6 12L7 8.425L8.4 7.025L12 10.625L15.575 7.025L16.975 8.425L13.375 12L16.975 15.6L15.575 17L12 13.4L8.4 17Z" fill="currentColor" />
    </g>
  </svg>
);

const SEARCH_DEBOUNCE_MS = 200;

export function HeaderSearchBar() {
  const [value, setValue] = useState('');
  const debouncedValue = useDebounce(value, SEARCH_DEBOUNCE_MS);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputRowRef = useRef<HTMLDivElement>(null);
  const trayContainerRef = useRef<HTMLDivElement>(null);
  const [trayRect, setTrayRect] = useState<{ top: number; left: number; width: number } | null>(null);
  // Tray-dismissed gate: an outside click or Esc collapses the tray
  // without clearing the query, so re-focusing or typing brings it back.
  const [trayDismissed, setTrayDismissed] = useState(false);

  // Measure the input row so the portaled tray (in document.body, to
  // escape the header's overflow-hidden) hangs directly under it. Tray
  // width + left both track the row, so the two stay edge-aligned.
  const updateTrayRect = useCallback(() => {
    const el = inputRowRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setTrayRect({
      top: rect.bottom,
      left: Math.max(8, rect.left), // clamp to 8px on narrow viewports
      width: rect.width,
    });
  }, []);

  // ResizeObserver catches width changes; resize/scroll catch viewport
  // changes. A ResizeObserver does NOT fire when the row only *moves*
  // (the search bar shifts horizontally when the centered nav reflows
  // after a font swap), so the tray is also re-measured on open below.
  useEffect(() => {
    const el = inputRowRef.current;
    if (!el) return;
    updateTrayRect();
    const ro = new ResizeObserver(updateTrayRect);
    ro.observe(el);
    window.addEventListener('resize', updateTrayRect);
    window.addEventListener('scroll', updateTrayRect, true);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateTrayRect);
      window.removeEventListener('scroll', updateTrayRect, true);
    };
  }, [updateTrayRect]);

  // Cmd-K (Mac) / Ctrl-K (Win/Linux) focuses the input from anywhere.
  // The input itself is always mounted, so this is a simple focus call
  // rather than a mode-switch.
  useGlobalHotkey('Mod+k', useCallback(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
    setTrayDismissed(false);
  }, []));

  const clearAndBlur = useCallback(() => {
    setValue('');
    inputRef.current?.blur();
    setTrayDismissed(false);
  }, []);

  // Outside click (anywhere not in the input row or the portaled tray)
  // dismisses the tray while preserving the query — re-focusing the
  // input or typing brings it back.
  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      const target = e.target as Node | null;
      if (!target) return;
      if (inputRowRef.current?.contains(target)) return;
      if (trayContainerRef.current?.contains(target)) return;
      setTrayDismissed(true);
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // SearchTray owns Enter (activates the highlighted result).
      // Esc dismisses the tray without clearing the query so the user
      // can re-focus the input and pick up where they left off.
      if (e.key === 'Escape') {
        setTrayDismissed(true);
        inputRef.current?.blur();
      }
    },
    []
  );

  const trayOpen = value.trim().length > 0 && !trayDismissed;

  // Re-measure each time the tray opens — by interaction time fonts have
  // settled, so this catches any post-mount horizontal shift the
  // ResizeObserver missed. useLayoutEffect runs before paint, no flicker.
  useLayoutEffect(() => {
    if (trayOpen) updateTrayRect();
  }, [trayOpen, updateTrayRect]);

  return (
    <>
      {/* No own background or stroke — the unified header container owns
          the surface; this row just hosts the input. */}
      <div
        ref={inputRowRef}
        className="flex items-center gap-2 h-14 w-full min-w-0"
      >
        <MagnifyingGlassIcon
          width="16"
          height="16"
          className="shrink-0"
          style={{ color: 'var(--gray-9)', marginLeft: '12px' }}
        />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search (Cmd+K)"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setTrayDismissed(false);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setTrayDismissed(false)}
          role="combobox"
          aria-expanded={value.trim().length > 0}
          aria-controls="header-search-tray"
          aria-autocomplete="list"
          className="flex-1 bg-transparent outline-none text-sm min-w-0"
          style={{
            color: 'var(--gray-12)',
            fontFamily: "'Geist Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
          }}
        />
        {value.length > 0 && (
          <IconButton
            variant="ghost"
            size="1"
            onClick={clearAndBlur}
            aria-label="Clear search"
            className="shrink-0"
          >
            <CloseSmallIcon />
          </IconButton>
        )}
      </div>
      {trayRect &&
        createPortal(
          <div
            ref={trayContainerRef}
            id="header-search-tray"
            className={searchTrayStyles.host}
            style={{
              position: 'fixed',
              top: trayRect.top,
              left: trayRect.left,
              // Explicit `right:auto` neutralises the `.host` rule's
              // `right:0` so left+width alone govern the fixed box.
              right: 'auto',
              width: trayRect.width,
              zIndex: 60,
            }}
          >
            <SearchTray
              // Tray opens once the user has typed and stays open until
              // either the input is cleared or the user clicks outside
              // (the dismiss gate). The CSS animates grid-template-rows
              // 0fr→1fr on this flip for a smooth collapse.
              open={trayOpen}
              query={value}
              debouncedQuery={debouncedValue}
              onActivateRecent={(q) => {
                setValue(q);
                setTrayDismissed(false);
                inputRef.current?.focus();
              }}
              onClose={clearAndBlur}
            />
          </div>,
          document.body
        )}
    </>
  );
}
